import * as React from 'react';
import type { ICategoryHeaderProps } from '../category-header';
import { CategoryHeader, dtoToCategoryHeaderProps } from '../category-header';
import type { ISolutionGridWrapperProps } from '../../solution-grid-wrapper/solution-grid-wrapper';
import { SolutionGridWrapper } from '../../solution-grid-wrapper/solution-grid-wrapper';
import s from './category.scss';
import type { ICategoriesCarouselComponentProps } from '../../home/partials/CategoriesCarousel';
import { CategoriesCarousel } from '../../home/partials/CategoriesCarousel';
import { composer } from '../../../component-decorators/composer';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { Box, Text } from '@wix/design-system';
import { CategoryHeaderMobile } from '../category-header-mobile/category-header-mobile';
import { useConfigContext } from '../../../config-context';
import { FluidContainer } from '../../FluidContainer';
import { getCollectionDefinition } from '@wix/app-market-collection-widget';
import type { GetWebSolutionsByTagsResponse } from '@wix/ambassador-marketplace/types';
import type { MarketplaceStore } from '../../../component-decorators/with-marketplace-store';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';

export interface ICategoryProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  backgroundColor: string;
  headerProps: ICategoryHeaderProps;
  solutionGridWrapperProps: ISolutionGridWrapperProps;
  appsCount: number;
  categoriesCarouselProps: ICategoriesCarouselComponentProps;
  solutionGridHighLowWrapperProps?: ISolutionGridWrapperProps;
  solutionGridLowHighWrapperProps?: ISolutionGridWrapperProps;
  appsPartOfSale?: ISolutionGridWrapperProps;
}

export const dtoToCategoryProps = (
  categoryRes: GetWebSolutionsByTagsResponse,
  webSolutionsRes: GetWebSolutionsByTagsResponse,
  categoriesListRes,
  t,
): ICategoryProps => {
  const slug = categoryRes.tagsAndSubCategories[0].tag.slug;
  const backgroundColor = categoryRes.tagsAndSubCategories[0].tag.colors[0];
  const headerProps = dtoToCategoryHeaderProps(categoryRes);
  const appsCount = webSolutionsRes.webSolutions.length;
  const solutionGridWrapperProps = {
    solutions: webSolutionsRes.webSolutions.map((solution, appIndex) => {
      return {
        appIndex,
        isPreInstalled: solution?.baseInfo?.showRegularAppPage,
        ...solution.baseInfo,
      };
    }),

    isCompany: true,
    referralSectionName: getCollectionDefinition(
      categoryRes.tagsAndSubCategories[0].tag,
    ).slug,
  };

  const getCategoryIcon = (category) =>
    category &&
    category.assetsMap &&
    category.assetsMap.collectionImage &&
    category.assetsMap.collectionImage.assets &&
    category.assetsMap.collectionImage.assets[0] &&
    category.assetsMap.collectionImage.assets[0].url;
  const categoriesCarouselProps = {
    displayName: t('category.exploreByCategory'),
    innerTagsDefinitions: categoriesListRes.categoryToSubCategory
      .map((category) => category.category)
      .filter((category) => !category.isHidden)
      .filter((category) => category.slug !== slug)
      .filter((category) => category.apps.length !== 0)
      .map((category) => {
        return {
          name: category.name,
          slug: category.slug,
          description: category.description,
          icon: getCategoryIcon(category),
        };
      }),
  };

  return {
    backgroundColor,
    headerProps,
    solutionGridWrapperProps,
    appsCount,
    categoriesCarouselProps,
  };
};

export const Category = composer()
  .withExperiments()
  .withMarketplaceStore()
  .withTranslation()
  .compose(
    ({
      backgroundColor,
      headerProps,
      solutionGridWrapperProps,
      appsCount,
      categoriesCarouselProps,
      t,
      marketplaceStore,
    }: ICategoryProps) => {
      const { isMobile } = useConfigContext();
      const [solutionGridProps, setSolutionGridProps] =
        React.useState<ISolutionGridWrapperProps>(solutionGridWrapperProps);
      const shouldShowBackButton =
        marketplaceStore?.prevRoute &&
        marketplaceStore.prevRoute.currentUrl !==
          marketplaceStore.route.currentUrl &&
        marketplaceStore?.route?.referral !== 'back_button' &&
        marketplaceStore?.route?.referral !== 'menu_navigation';
      React.useLayoutEffect(() => {
        setSolutionGridProps(solutionGridWrapperProps);
      }, [solutionGridWrapperProps]);
      React.useEffect(() => {}, [solutionGridProps]);

      return (
        <div data-testid="category">
          <Box
            className={s.categoryHeaderBackground}
            backgroundColor={backgroundColor}
          >
            {isMobile ? (
              <CategoryHeaderMobile
                {...headerProps}
                shouldShowBackButton={shouldShowBackButton}
              />
            ) : (
              <CategoryHeader
                {...headerProps}
                shouldShowBackButton={shouldShowBackButton}
              />
            )}
          </Box>
          <FluidContainer className={s.categoryContent}>
            <Box
              className={s.appsCount}
              direction="vertical"
              align="space-between"
            >
              <Text
                className={s.appsCountText}
                tagName="h2"
                dataHook="amount-apps"
              >
                {t('collection.apps-counter', { appsCount })}{' '}
              </Text>
            </Box>
            <SolutionGridWrapper {...solutionGridProps} />
          </FluidContainer>
          <CategoriesCarousel {...categoriesCarouselProps} />
        </div>
      );
    },
  );
